import type { ReactNode } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/modal.tsx";
import { useUnsavedGuard } from "../../hooks/useUnsavedGuard.ts";
import formStyles from "../../styles/formPage.module.scss";

export interface GridEditModalStatus {
  type: "success" | "error" | "warning" | "info";
  message: string;
}

export interface GridEditModalProps {
  // ── Required ─────────────────────────────────
  title: string;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  children: ReactNode;

  // ── State ────────────────────────────────────
  /** Default true. Pass `false` to render nothing; most callers mount conditionally instead. */
  open?: boolean;
  /** Disables Save + swaps label to savingLabel. */
  saving?: boolean;
  /** Replaces children with a loading stub; Save stays disabled. */
  loading?: boolean;

  // ── Inline feedback ──────────────────────────
  statusMessage?: GridEditModalStatus | null;

  // ── Footer customisation ─────────────────────
  /** Rendered in ModalFooter on the LEFT (destructive actions like Delete, or extra toggles). */
  footerLeft?: ReactNode;
  saveLabel?: string;
  savingLabel?: string;
  cancelLabel?: string;
  /** Hide Save entirely (read-only modal). Footer becomes Close-only. */
  readOnly?: boolean;

  // ── Presentation ─────────────────────────────
  wide?: boolean;
  /** Override Modal width beyond `wide` — for multi-column or RMA-class editors. */
  maxWidth?: string | number;
  /**
   * Sets a `min-height` on the modal body container. Use for editors whose
   * tab list grows on first save (Create→Edit reveals more tabs) so the
   * body doesn't visibly shrink/grow on the transition.
   */
  minBodyHeight?: string | number;

  // ── Unsaved-changes guard (opt-in) ───────────
  /**
   * When true, closing the modal (X / Cancel / backdrop / Esc) prompts the user
   * to confirm via `useAlertDialog`. Also surfaces an inline status banner —
   * info severity, message defaults to "Unsaved changes" or `unsavedSummary`.
   * `statusMessage` (passed explicitly) wins over the auto-derived banner.
   */
  unsaved?: boolean;
  /** Inline text shown in the auto banner when `unsaved` is true (e.g. "3 unsaved tabs"). */
  unsavedSummary?: string;
  /** When true, a warning banner replaces the unsaved one — discard prompt is skipped. */
  stale?: boolean;
  /** Inline text for the stale banner. */
  staleSummary?: string;
}

const STATUS_BG: Record<GridEditModalStatus["type"], string> = {
  success: "var(--color-success-muted)",
  error: "var(--color-error-muted)",
  warning: "var(--color-warning-muted)",
  info: "var(--color-info-muted)",
};

const STATUS_FG: Record<GridEditModalStatus["type"], string> = {
  success: "var(--color-success)",
  error: "var(--color-error)",
  warning: "var(--color-warning)",
  info: "var(--color-info)",
};

function defaultSavingLabel(saveLabel: string): string {
  const s = saveLabel.trim().toLowerCase();
  if (s === "create" || s === "add" || s === "new") return "Creating…";
  if (s === "apply") return "Applying…";
  if (s === "send") return "Sending…";
  return "Saving…";
}

/**
 * Wraps the Modal chrome mandated by AGENTS.md § Create/Edit Modal Guidelines
 * into a single component. Use from any editor launched off a DataGrid row or
 * a grid toolbar "+ Create" button.
 *
 * See AGENTS.md § Dialog-over-grid pattern for the canonical caller shape;
 * prefer composing with `useGridEditDialog` so state + URL handoff are reused.
 */
export function GridEditModal({
  title,
  onClose,
  onSave,
  children,
  open = true,
  saving = false,
  loading = false,
  statusMessage = null,
  footerLeft,
  saveLabel = "Save",
  savingLabel,
  cancelLabel = "Cancel",
  readOnly = false,
  wide,
  maxWidth,
  minBodyHeight,
  unsaved = false,
  unsavedSummary,
  stale = false,
  staleSummary,
}: GridEditModalProps) {
  const resolvedSavingLabel = savingLabel ?? defaultSavingLabel(saveLabel);
  // The guard owns the discard-confirm + auto-banner. Explicit `statusMessage`
  // still wins so callers can paint custom feedback (e.g. save error) without
  // losing the unsaved hint flow.
  const guard = useUnsavedGuard({
    dirty: unsaved,
    dirtySummary: unsavedSummary,
    stale,
    staleSummary,
    onClose,
  });
  const effectiveStatus = statusMessage ?? guard.status;

  return (
    <Modal open={open} onClose={guard.attemptClose} wide={wide} maxWidth={maxWidth}>
      <ModalHeader title={title} onClose={guard.attemptClose} />
      <ModalBody>
        <div
          style={
            minBodyHeight != null
              ? { minHeight: typeof minBodyHeight === "number" ? `${minBodyHeight}px` : minBodyHeight }
              : undefined
          }
        >
          {loading ? (
            <div
              style={{
                padding: "var(--space-6) var(--space-4)",
                textAlign: "center",
                color: "var(--color-text-muted)",
              }}
            >
              Loading…
            </div>
          ) : (
            children
          )}
        </div>
      </ModalBody>
      {/* Status banner sits as its own flex sibling between body and footer
          so editors that bleed body content past ModalBody's padding (e.g.
          full-frame sidebar layouts via negative margins) can't paint over
          it. flex: 0 0 auto pins it; the body still owns the scroll. */}
      {effectiveStatus && (
        <div
          role={effectiveStatus.type === "error" ? "alert" : "status"}
          style={{
            flex: "0 0 auto",
            padding: "var(--space-2) var(--space-5)",
            fontSize: "var(--text-sm)",
            borderTop: "1px solid var(--color-border-default)",
            background: STATUS_BG[effectiveStatus.type],
            color: STATUS_FG[effectiveStatus.type],
          }}
        >
          {effectiveStatus.message}
        </div>
      )}
      <ModalFooter>
        {footerLeft != null && (
          <>
            <div style={{ display: "inline-flex", gap: "var(--space-2)" }}>{footerLeft}</div>
            <div style={{ flex: 1 }} />
          </>
        )}
        <button type="button" className={formStyles.secondaryBtn} onClick={guard.attemptClose}>
          {cancelLabel}
        </button>
        {!readOnly && (
          <button
            type="button"
            className={formStyles.primaryBtn}
            onClick={onSave}
            disabled={saving || loading}
          >
            {saving ? resolvedSavingLabel : saveLabel}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
